import { useCallback, useState } from 'react';

/**
 * Tracks whether a form differs from its last-saved baseline, so a Save button
 * can be disabled when there are no unsaved changes.
 *
 * Minimal retrofit — keep your existing `[form, setForm]`:
 *   const { isDirty, markSaved, resetBaseline } = useDirtyForm(form);
 *   // when the form is (re)seeded from server data (load effect / entity switch):
 *   useEffect(() => { setForm(loaded); resetBaseline(loaded); }, [loaded]);
 *   // Save button:
 *   <button disabled={!isDirty || saving} ... />
 *   // after a successful save:
 *   markSaved(); toast.success('Guardado');
 *
 * Comparison is a stable JSON stringify of the SAME-shaped object across edits,
 * which is reliable for form state (fixed key set). Nested objects/arrays are
 * compared by value too.
 */
export function useDirtyForm<T>(form: T) {
  const [savedSnapshot, setSavedSnapshot] = useState<string>(() => stableStringify(form));

  const isDirty = stableStringify(form) !== savedSnapshot;

  // Call after a successful save (baseline becomes the current form).
  const markSaved = useCallback(() => setSavedSnapshot(stableStringify(form)), [form]);

  // Call when the form is (re)seeded from server data, so freshly-loaded data
  // isn't reported as "changed".
  const resetBaseline = useCallback((value: T) => setSavedSnapshot(stableStringify(value)), []);

  return { isDirty, markSaved, resetBaseline };
}

// JSON.stringify with sorted keys so key ordering never affects equality.
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_k, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.keys(v).sort().reduce((acc, key) => { (acc as Record<string, unknown>)[key] = (v as Record<string, unknown>)[key]; return acc; }, {} as Record<string, unknown>)
      : v
  );
}
